import axios from 'axios';
import { store } from '../store/store';
import { selectTenant, selectUserId, selectUserRole } from '../store/features/Auth/authSlice';
import { getVariant, type Variant } from './abTest/variant';
import {getAccessToken} from './authRuntime';
import {isStorageQuotaError, setChatStorageReporter} from '../utils/chatStorage';
import {setMatrixSessionLockReporter} from './matrixSessionLock';
import {OFFLINE_LOGS_KEY, onOfflineLogsCleared} from '../utils/logStorage';
import {
  SESSION_ID,
  getCallContextSnapshot,
} from './callCorrelation';

// Configuration constants
const BATCH_SIZE = 10;
const MAX_BUFFER_SIZE = 1000;
const FLUSH_TIMEOUT = 30000; // 30 seconds
// localStorage gives a whole origin ~5 MB; the offline log must never be what
// pushes chat's own keys (session lock, device id) over the quota.
// Counted in string length (UTF-16 chars), not bytes; non-ASCII text can take up to ~3x in storage.
const MAX_STORED_LOGS_BYTES = 512 * 1024;
const STORAGE_WRITE_DEBOUNCE_MS = 1000;
const MAX_ERROR_STACK_CHARS = 2048;
const MAX_SERIALIZE_DEPTH = 5;

/**
 * Get LOGSTASH_URL dynamically to ensure it's read after EnvironmentProvider loads
 */
const getLogstashUrl = (): string | undefined => {
  return window.__ENV__?.LOGSTASH_URL || import.meta.env.VITE_LOGSTASH_URL;
};

// Log levels enum
export enum LogLevel {
  DEBUG = 'debug',
  INFO = 'info',
  WARN = 'warn',
  ERROR = 'error'
}

// Context interface for structured logging
export interface LogContext {
  source?: string;
  event?: string;
  component?: string;
  userId?: string;
  tenant?: string;
  sessionId?: string;
  callId?: string;
  roomId?: string;
  contactId?: string;
  phoneNumber?: string;
  integration?: string;
  [key: string]: any;
}

// ECS-compliant log entry interface
interface LogEntry {
  event_id: string;
  '@timestamp': string;
  'ecs.version': string;
  timestamp_unix: number;
  'log.level': string;
  'log.logger': string;
  message: string;
  event: {
    dataset: string;
    module: string;
    action: string;
    category: string;
    type: string[];
  };
  service: {
    name: string;
    type: string;
    version: string;
    environment: string;
  };
  variant?: Variant;
  user?: {
    id: string;
    role?: string;
  };
  organization?: {
    id: string;
    name: string;
  };
  client: {
    platform: string;
    type: string;
    browser?: string;
    version?: string;
  };
  user_agent?: {
    original: string;
  };
  url?: {
    path: string;
    query?: string;
    hash?: string;
  };
  communication?: {
    direction?: string;
    from?: string;
    to?: string;
    room_id?: string;
  };
  labels: {
    source: string;
    event: string;
    tenant: string;
    user_id: string;
    component?: string;
    [key: string]: any;
  };
  context: LogContext;
}

// Global state
let logBuffer: LogEntry[] = [];
let isFlushingLogs = false;
let flushTimer: ReturnType<typeof setTimeout> | null = null;

// Store native console methods before override
const nativeConsole = {
  log: console.log.bind(console),
  error: console.error.bind(console),
  warn: console.warn.bind(console),
  info: console.info.bind(console),
  debug: console.debug.bind(console),
};

let storageWriteTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Turn an Error into plain JSON. JSON.stringify(new Error('x')) is "{}", which
 * is how a real chat-init failure reached Kibana with no message or stack.
 */
export const serializeError = (error: Error, depth = 0, seen = new WeakSet<object>()): Record<string, unknown> => {
  seen.add(error);
  const out: Record<string, unknown> = {
    name: error.name,
    message: error.message,
  };
  if (typeof error.stack === 'string') {
    out.stack = error.stack.length > MAX_ERROR_STACK_CHARS
      ? `${error.stack.slice(0, MAX_ERROR_STACK_CHARS)}...[truncated]`
      : error.stack;
  }
  // Own enumerable fields carry the useful part of SDK/RTK errors (errcode,
  // httpStatus, data.retry_after_ms, storedDeviceId, key ...).
  for (const key of Object.keys(error)) {
    if (key in out || key === 'cause') continue;
    const value = (error as unknown as Record<string, unknown>)[key];
    if (typeof value === 'string') {
      out[key] = value.length > MAX_ERROR_STACK_CHARS ? `${value.slice(0, MAX_ERROR_STACK_CHARS)}...[truncated]` : value;
    } else if (typeof value === 'number' || typeof value === 'boolean') {
      out[key] = value;
    } else if (value !== null && typeof value === 'object') {
      out[key] = sanitizeValue(value, depth + 1, seen);
    }
  }
  const cause = (error as { cause?: unknown }).cause;
  if (cause !== undefined) out.cause = sanitizeValue(cause, depth + 1, seen);
  return out;
};

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  if (value === null || typeof value !== 'object') return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
};

/**
 * Replace Error instances anywhere in a context with serialisable objects.
 * Only plain objects and arrays are walked; everything else keeps its shape.
 */
const sanitizeValue = (value: unknown, depth: number, seen: WeakSet<object>): unknown => {
  if (value instanceof Error) {
    if (seen.has(value) || depth > MAX_SERIALIZE_DEPTH) return { name: value.name, message: value.message };
    return serializeError(value, depth, seen);
  }
  if (depth > MAX_SERIALIZE_DEPTH || value === null || typeof value !== 'object') return value;
  if (seen.has(value)) return value;
  if (Array.isArray(value)) {
    seen.add(value);
    const mapped = value.map(item => sanitizeValue(item, depth + 1, seen));
    seen.delete(value);
    return mapped;
  }
  if (isPlainObject(value)) {
    seen.add(value);
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(value)) {
      out[key] = sanitizeValue(value[key], depth + 1, seen);
    }
    seen.delete(value);
    return out;
  }
  return value;
};

export const sanitizeLogContext = (context: LogContext): LogContext =>
  sanitizeValue(context, 0, new WeakSet()) as LogContext;

/**
 * Drop the oldest entries until the serialised buffer fits the byte cap.
 */
const trimBufferToCap = (maxBytes: number): string => {
  let serialized = JSON.stringify(logBuffer);
  if (serialized.length <= maxBytes) return serialized;
  const sizes = logBuffer.map(entry => JSON.stringify(entry).length + 1);
  let total = sizes.reduce((sum, size) => sum + size, 2);
  let drop = 0;
  while (total > maxBytes && drop < logBuffer.length - 1) {
    total -= sizes[drop];
    drop += 1;
  }
  logBuffer = logBuffer.slice(drop);
  serialized = JSON.stringify(logBuffer);
  return serialized;
};

/**
 * Save logs to localStorage for offline support. Never throws: on quota
 * pressure it drops the oldest logs, and in the end drops logs rather than
 * failing the caller.
 */
const saveLogsToStorage = (): void => {
  if (storageWriteTimer) {
    clearTimeout(storageWriteTimer);
    storageWriteTimer = null;
  }
  try {
    localStorage.setItem(OFFLINE_LOGS_KEY, trimBufferToCap(MAX_STORED_LOGS_BYTES));
    return;
  } catch (error) {
    if (!isStorageQuotaError(error)) {
      nativeConsole.error('Failed to save logs to localStorage', error);
      return;
    }
  }
  // Quota: keep only the newest half and retry once, then give up on persisting.
  logBuffer = logBuffer.slice(Math.floor(logBuffer.length / 2));
  try {
    localStorage.setItem(OFFLINE_LOGS_KEY, JSON.stringify(logBuffer));
  } catch {
    logBuffer = [];
    try {
      localStorage.removeItem(OFFLINE_LOGS_KEY);
    } catch {
      // storage is unusable; logs stay in memory only
    }
    nativeConsole.warn('localStorage quota exceeded; dropped offline logs');
  }
};

/**
 * Coalesce per-line writes: serialising up to the whole buffer on every log
 * line is what made the logger a quota and CPU hazard.
 */
const scheduleSaveLogsToStorage = (): void => {
  if (storageWriteTimer) return;
  storageWriteTimer = setTimeout(() => {
    storageWriteTimer = null;
    saveLogsToStorage();
  }, STORAGE_WRITE_DEBOUNCE_MS);
};

/**
 * Load logs from localStorage on app start
 */
const loadLogsFromStorage = (): void => {
  try {
    const storedLogs = localStorage.getItem(OFFLINE_LOGS_KEY);
    if (storedLogs) {
      const parsedLogs = JSON.parse(storedLogs);
      if (Array.isArray(parsedLogs)) {
        logBuffer = parsedLogs;
        nativeConsole.info(`Loaded ${logBuffer.length} logs from storage`);
        
        // Schedule flush of stored logs
        if (logBuffer.length > 0) {
          setTimeout(() => {
            flushLogs().catch(error => {
              nativeConsole.error('Failed to flush loaded logs:', error);
            });
          }, 1000);
        }
      }
    }
  } catch {
    try {
      localStorage.removeItem(OFFLINE_LOGS_KEY);
    } catch {
      // storage inaccessible; run with in-memory logs only
    }
  }
};

/**
 * Flush logs to logstash server
 */
const flushLogs = async (): Promise<void> => {
  if (isFlushingLogs || logBuffer.length === 0) {
    return;
  }

  isFlushingLogs = true;
  const logsToFlush = [...logBuffer];
  logBuffer = [];

  try {
    const logstashUrl = getLogstashUrl();
    if (logstashUrl && logsToFlush.length > 0) {
      let authToken = '';
      try {
        authToken = getAccessToken() || '';
      } catch (error) {
        nativeConsole.warn('Failed to get auth token for logging:', error);
      }

      await Promise.race([
        axios.post(logstashUrl, logsToFlush, {
          timeout: FLUSH_TIMEOUT,
          headers: {
            'Content-Type': 'application/json',
            ...(authToken && { 'Authorization': `Bearer ${authToken}` }),
          },
        }),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Flush timeout')), FLUSH_TIMEOUT)
        ),
      ]);

      nativeConsole.info(`Successfully flushed ${logsToFlush.length} logs to server`);
    }

    saveLogsToStorage();
  } catch (error) {
    // Restore logs to buffer on failure
    logBuffer = [...logsToFlush, ...logBuffer];

    // Prevent memory leaks
    if (logBuffer.length > MAX_BUFFER_SIZE) {
      const removeCount = logBuffer.length - MAX_BUFFER_SIZE;
      logBuffer = logBuffer.slice(removeCount);
      nativeConsole.warn(`Removed ${removeCount} old logs to prevent memory leak`);
    }

    saveLogsToStorage();
    nativeConsole.error('Failed to flush logs to server:', (error as any)?.message || error);
  } finally {
    isFlushingLogs = false;
  }
};

/**
 * Map source to ECS event category
 */
const getCategoryFromSource = (source?: string): string => {
  if (!source) return 'process';
  
  if (source.includes('matrix') || source.includes('chat')) return 'network';
  if (source.includes('sip') || source.includes('call')) return 'process';
  if (source.includes('auth') || source.includes('keycloak')) return 'authentication';
  if (source.includes('navigation') || source.includes('router')) return 'web';
  
  return 'process';
};

/**
 * Map event and level to ECS event type
 */
const getEventType = (event?: string, level?: string): string[] => {
  const types: string[] = [];
  
  if (!event) return ['info'];
  
  if (event.includes('connect') || event.includes('login') || event.includes('join')) {
    types.push('connection');
  }
  
  if (event.includes('call') || event.includes('session') || event.includes('start')) {
    types.push('start');
  }
  
  if (event.includes('end') || event.includes('complete') || event.includes('logout')) {
    types.push('end');
  }
  
  if (level === 'error' || event.includes('error') || event.includes('fail')) {
    types.push('error');
  }
  
  if (event.includes('user') || event.includes('click') || event.includes('interaction')) {
    types.push('user');
  }
  
  return types.length > 0 ? types : ['info'];
};

/**
 * Get browser information with proper detection order and ECS compliance
 */
const getBrowserInfo = () => {
  const userAgent = navigator.userAgent;
  let browser = 'unknown';
  let version = 'unknown';
  
  // Check for Edge first (before Chrome) since Edge contains "Chrome" in its user agent
  if (userAgent.includes('OPR/')) {
    browser = 'opera';
    const match = userAgent.match(/OPR\/([0-9.]+)/);
    version = match ? match[1] : 'unknown';
  }
  else if (userAgent.includes('Edg/')) {
    browser = 'edge';
    const match = userAgent.match(/Edg\/([0-9.]+)/);
    version = match ? match[1] : 'unknown';
  }
  // Check for Chrome (but not Edge)
  else if (userAgent.includes('Chrome/')) {
    browser = 'chrome';
    const match = userAgent.match(/Chrome\/([0-9.]+)/);
    version = match ? match[1] : 'unknown';
  }
  // Check for Firefox
  else if (userAgent.includes('Firefox/')) {
    browser = 'firefox';
    const match = userAgent.match(/Firefox\/([0-9.]+)/);
    version = match ? match[1] : 'unknown';
  }
  // Check for Safari (but not Chrome-based browsers)
  else if (userAgent.includes('Safari/') && !userAgent.includes('Chrome/')) {
    browser = 'safari';
    const match = userAgent.match(/Version\/([0-9.]+)/);
    version = match ? match[1] : 'unknown';
  }
  
  return { 
    browser, 
    version,
    userAgent // Full user agent for ECS compliance
  };
};

const newEventId = (): string =>
  globalThis.crypto?.randomUUID?.() ?? `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

/**
 * Create ECS-compliant log entry
 */
const createLogEntry = (level: LogLevel, message: string, inputContext: LogContext = {}): LogEntry => {
  // Identities are merged in here rather than at each call site, so every
  // existing logger.info in the app starts carrying them unchanged. An
  // explicit field always wins - a caller that knows the real call_id must
  // not be overwritten by a stale context.
  const context: LogContext = {
    ...getCallContextSnapshot(),
    session_id: SESSION_ID,
    ...sanitizeLogContext(inputContext),
  };
  const timestamp = new Date();
  const isoTimestamp = timestamp.toISOString();
  const unixTimestamp = Math.floor(timestamp.getTime() / 1000);

  // Get store state safely
  let userId: string = '';
  let tenant: string = '';
  let userRole: string = '';

  try {
    const state = store.getState();
    const selectedUserId = selectUserId(state as any);
    const selectedTenant = selectTenant(state as any);
    const selectedUserRole = selectUserRole(state as any);
    
    userId = selectedUserId || '';
    tenant = selectedTenant || '';
    userRole = selectedUserRole || '';
  } catch (storeError) {
    nativeConsole.warn('Store not available during logging:', storeError);
  }

  const browserInfo = getBrowserInfo();

  return {
    event_id: newEventId(),
    '@timestamp': isoTimestamp,
    'ecs.version': '8.6.0',
    timestamp_unix: unixTimestamp,
    'log.level': level,
    'log.logger': 'webapp',
    message,
    event: {
      dataset: 'webapp.logs',
      module: context.source || 'unknown',
      action: context.event || 'log',
      category: getCategoryFromSource(context.source),
      type: getEventType(context.event, level),
    },
    service: {
      name: 'webapp',
      type: 'webapp',
      version: __APP_VERSION__,
      environment: import.meta.env.MODE,
    },
    variant: getVariant() ?? undefined,
    user: userId ? {
      id: userId,
      role: userRole,
    } : undefined,
    organization: tenant ? {
      id: tenant,
      name: tenant,
    } : undefined,
    client: {
      platform: 'web',
      type: 'browser',
      browser: browserInfo.browser,
      version: browserInfo.version,
    },
    user_agent: {
      original: browserInfo.userAgent,
    },
    url: {
      path: window.location.pathname,
      query: window.location.search,
      hash: window.location.hash,
    },
    communication: {
      direction: context.direction,
      from: context.from || context.phoneNumber,
      to: context.to,
      room_id: context.roomId,
    },
    labels: {
      source: context.source || 'unknown',
      event: context.event || 'log',
      tenant: tenant || 'unknown',
      user_id: userId || 'unknown',
      component: context.component,
      // Same key names MobileCh3 uses, so one Kibana query joins a backend
      // event to whichever client handled it. See CallCorrelation above.
      trace_id: context.trace_id,
      call_id: context.call_id,
      attempt_id: context.attempt_id,
      session_id: context.session_id,
      ...context,
    },
    context,
  };
};

/**
 * Add log entry to buffer and handle batching
 */
const addToBuffer = (logEntry: LogEntry): void => {
  logBuffer.push(logEntry);
  if (logBuffer.length > MAX_BUFFER_SIZE) {
    logBuffer = logBuffer.slice(logBuffer.length - MAX_BUFFER_SIZE);
  }
  scheduleSaveLogsToStorage();

  // Flush when batch size is reached
  if (logBuffer.length >= BATCH_SIZE) {
    flushLogs().catch(error => {
      nativeConsole.error('Failed to flush logs:', error);
    });
  }

  // Set up periodic flush
  if (flushTimer) {
    clearTimeout(flushTimer);
  }
  flushTimer = setTimeout(() => {
    if (logBuffer.length > 0) {
      flushLogs().catch(error => {
        nativeConsole.error('Failed to flush logs in periodic flush:', error);
      });
    }
  }, 60000); // Flush every 60 seconds
};

/**
 * Logger class
 */
class Logger {
  debug(message: string, context?: LogContext): void;
  debug(context: LogContext, message: string): void;
  debug(messageOrContext: string | LogContext, contextOrMessage?: LogContext | string): void {
    const { message, context } = this.parseArguments(messageOrContext, contextOrMessage);
    const logEntry = createLogEntry(LogLevel.DEBUG, message, context);
    nativeConsole.debug(message, context);
    addToBuffer(logEntry);
  }

  info(message: string, context?: LogContext): void;
  info(context: LogContext, message: string): void;
  info(messageOrContext: string | LogContext, contextOrMessage?: LogContext | string): void {
    const { message, context } = this.parseArguments(messageOrContext, contextOrMessage);
    const logEntry = createLogEntry(LogLevel.INFO, message, context);
    nativeConsole.info(message, context);
    addToBuffer(logEntry);
  }

  warn(message: string, context?: LogContext): void;
  warn(context: LogContext, message: string): void;
  warn(messageOrContext: string | LogContext, contextOrMessage?: LogContext | string): void {
    const { message, context } = this.parseArguments(messageOrContext, contextOrMessage);
    const logEntry = createLogEntry(LogLevel.WARN, message, context);
    nativeConsole.warn(message, context);
    addToBuffer(logEntry);
  }

  error(message: string, context?: LogContext): void;
  error(context: LogContext, message: string): void;
  error(messageOrContext: string | LogContext, contextOrMessage?: LogContext | string): void {
    const { message, context } = this.parseArguments(messageOrContext, contextOrMessage);
    const logEntry = createLogEntry(LogLevel.ERROR, message, context);
    nativeConsole.error(message, context);
    addToBuffer(logEntry);
  }

  private parseArguments(
    messageOrContext: string | LogContext,
    contextOrMessage?: LogContext | string
  ): { message: string; context: LogContext } {
    if (typeof messageOrContext === 'string') {
      return {
        message: messageOrContext,
        context: (contextOrMessage as LogContext) || {},
      };
    } else {
      return {
        message: (contextOrMessage as string) || '',
        context: messageOrContext,
      };
    }
  }
}

// Initialize logger
const logger = new Logger();

onOfflineLogsCleared(() => {
  logBuffer = [];
});
setChatStorageReporter((level, message, context) => logger[level](message, context));
setMatrixSessionLockReporter((level, message, context) => logger[level](message, context));

// Load stored logs on module load
loadLogsFromStorage();

// Debounced writes must not be lost when the tab goes away
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => {
    if (storageWriteTimer) saveLogsToStorage();
  });
}

// Log initialization
logger.info('Web application logger initialized', {
  source: 'logger',
  event: 'init',
  environment: import.meta.env.MODE,
  hasLogstashUrl: !!getLogstashUrl()
});

export default logger;
